/* Step 7a (owner card "AZEX Step 7 Card V2", 29-Oct-2026) — helpers of the sample evaluation panel: amounts, the method
   label a stored name shows (the old specific-vouching name shows the new reference), the working-paper lines in EN + AR built
   from the evaluation state's structured fields (never from the back end's English sentences). */
import { fmtNumber, fmtCount } from "@/components/Amount";

export const SPECIFIC_NEW = "Specific vouching - ISA 500.A55–A56";
export const SPECIFIC_OLD = "Specific vouching - ISA 500.A53";
export const isSpecific = (m) => m === SPECIFIC_NEW || m === SPECIFIC_OLD;

/** Step 12-F5a · G7b — a standard as the screens show it: the name without a paragraph number ("ISA 500 A54" → "ISA 500",
 *  "ISA 530 para 8; ISA 500 A55–A56" → "ISA 530; ISA 500"); samples drawn before keep the old text in their stored stats. */
export const plainStandard = (s) => String(s || "").replace(
  /(\b(?:ISA|ISQM|IAS|IFRS) ?\d{3})(?:\s*(?:paras?|paragraphs?|¶|§)?\s*\.?\s*A?\d+(?:\([a-z]\))?(?:\s*[–-]\s*A?\d+)?)/g, "$1");

export const amt = (v) => (v == null || v === "" ? "—" : fmtNumber(Number(v)));
export const cnt = (v) => (v == null || v === "" ? "—" : fmtCount(Number(v)));
export const fill = (s, vals) => Object.entries(vals || {}).reduce((acc, [k, v]) => acc.split(`{${k}}`).join(String(v)), String(s || ""));

/** The method as it shows: a translated name, the corrected specific-vouching reference (OQ-3), else the stored name. */
export function methodText(method, t) {
  // the specific-vouching names hold a dot ("500.A55"), which a dotted i18n path cannot address — its own key
  if (isSpecific(method)) return t("s7a.specificMethod") || SPECIFIC_NEW;
  const k = `samplingMethods.${method}`;
  const v = t(k);
  return v && v !== k ? v : (method || "");
}

/** The selection-kind line of a sample (approach A, S14, S16) or "" for a sample that is projected. */
export function kindLine(sample, t) {
  const st = sample?.stats || {};
  if (isSpecific(sample?.method) || (st.not_a_sample && st.no_projection)) return t("s7a.specificLine");
  if (sample?.method === "Risk-Based" || st.selection_kind === "risk_based") return t("s7a.riskLine");
  if (st.hundred_pct_tested || st.hundred_pct_certainty || st.coverage_label === "100 % examination – not a sample") return t("s7a.hundredPct");
  return "";
}

const ORDER_KEYS = { "entry date, voucher no., account code, line id": "s7a.populationOrderGl",
                     "listing order (as uploaded)": "s7a.populationOrderListing" };

/** The working-paper lines (S1, S2, S4, S10, S14, approach A / B / C) of an evaluation state, in the screen language. */
export function workingPaperLines(state, t, lang) {
  if (!state) return [];
  const wp = state.working_paper || {};
  const out = [];
  const kind = state.method_kind;
  if (kind === "not_a_sample") {
    out.push({ key: "kind", text: isSpecific(state.method) ? t("s7a.specificLine") : t("s7a.riskLine") });
    out.push({ key: "rest", text: t("s7a.restOfPopulation") });
  } else if (kind === "hundred_pct") {
    out.push({ key: "kind", text: `${t("s7a.hundredPct")} (ISA 500)` });   // Step 12-F5a: no unverified paragraph number
  } else if (kind === "ratio") {
    out.push({ key: "kind", text: t("s7a.ratioText") });
  } else if (kind === "mus") {
    out.push({ key: "kind", text: t("s7a.musText") });
  }
  if (state.population_size != null || state.population_value != null) {
    out.push({ key: "pop", text: fill(t("s7a.panel.populationLine"), { n: cnt(state.population_size), value: amt(state.population_value) }) });
  }
  if (state.population_order) {
    const ok = ORDER_KEYS[state.population_order];
    out.push({ key: "order", text: `${t("s7a.panel.populationOrder")}: ${ok ? t(ok) : state.population_order}` });
  }
  if (wp.interval != null && wp.random_start != null) {
    // the stored chance is an English sentence ("6 ÷ 32 for every item") — the numbers go into the screen language (7a-4)
    const m = /^(\d+)\s*÷\s*(\d+)/.exec(String(wp.selection_probability || ""));
    const p = m ? fill(t("s7a.panel.chanceEvery"), { n: m[1], N: m[2] }) : "";
    out.push({ key: "interval", text: fill(t("s7a.panel.intervalLine"), { k: Number(wp.interval).toFixed(4), s: Number(wp.random_start).toFixed(4),
                                                                          p }) });
  }
  if (wp.earlier_interval_rule) out.push({ key: "earlierInterval", text: t("s7a.earlierIntervalRule") });
  if (state.credit_items || state.zero_items != null) {
    const ci = state.credit_items || {};
    out.push({ key: "credit", text: `${t("s7a.creditZeroTreatment")} ${fill(t("s7a.panel.creditZeroCounts"),
                                                                        { n: cnt(ci.n || 0), value: amt(ci.value || 0), z: cnt(state.zero_items || 0) })}` });
  }
  if (wp.working_hours_note) {
    out.push({ key: "hours", text: lang === "ar" && wp.working_hours_note_ar ? wp.working_hours_note_ar : t("s7a.workingHoursNotSet") });
  } else if (wp.working_hours_used && wp.working_hours_used.start_hour != null) {
    const p2 = (h) => String(h).padStart(2, "0");
    out.push({ key: "hours", text: fill(t("s7a.panel.hoursUsed"), { start: p2(wp.working_hours_used.start_hour), end: p2(wp.working_hours_used.end_hour) }) });
  }
  return out;
}

export const statusKey = { pending: "s7a.statusPending", tested: "s7a.statusTested", not_applicable: "s7a.statusNotApplicable",
                           untestable: "s7a.statusUntestable" };
